/**
 * Live loudness, for anything that draws.
 *
 * WHY THIS IS NOT IN THE SNAPSHOT, which is the only decision in this file that
 * matters. `CallSnapshot` already carries `speaking: { user, assistant }` — two
 * booleans, derived from the provider's VAD events, replaced a few times a second
 * and consumed by React through `useSyncExternalStore`. That is exactly right for a
 * badge that says "listening".
 *
 * It is useless for an orb. An animation needs a CONTINUOUS amplitude at frame
 * rate, and putting a value that changes sixty times a second into the snapshot
 * would re-render every subscribed component sixty times a second — for a number
 * that only one canvas cares about. So the meter lives beside the snapshot and is
 * READ, not pushed: a consumer already running a `requestAnimationFrame` loop calls
 * `read()` inside it and nothing else in the application notices.
 *
 * WHY IT MEASURES BOTH DIRECTIONS SEPARATELY. An orb that reacts to "audio" is an
 * orb that pulses when the user speaks and again when the agent replies, with no
 * way to tell the two apart — which reads as lag. Two independent levels let a
 * component show the user's voice and the agent's voice as visually different
 * things, which is the whole point of having a visualiser rather than a spinner.
 *
 * SHARES THE CALL'S AudioContext ON PURPOSE. A second context costs a second
 * hardware audio thread and, on Safari, can fail to start at all when the first
 * one already holds the output. The recorder's context is already resumed inside
 * the user gesture (see the start sequence in index.ts), so borrowing it means the
 * analyser is live from the first frame rather than silently suspended. The cost is
 * that when there is no context — the fallback path — there are no levels, and
 * `read()` returns zero. A visualiser must therefore degrade to the booleans rather
 * than assume a number is available; the orb does.
 *
 * NOTHING HERE TOUCHES THE RECORDING. `createMediaStreamSource` taps a stream
 * without consuming it, and the analyser is a leaf — connected to nothing
 * downstream, so it cannot route conversation audio anywhere or alter what the
 * recorder sees. Deliberately NOT `createMediaElementSource`, which REPLACES the
 * element's own output routing: calling it on the `<audio>` element that is playing
 * the agent would silence the agent unless the graph were reconnected to the
 * destination, and getting that wrong is a silent call.
 */
import type { TrackName } from './types'

/** Frequency-domain size. 512 bins is enough to compute a stable RMS and small
 *  enough that reading it every frame is free. Nothing here looks at spectrum. */
const FFT_SIZE = 512;

/**
 * How fast a level may fall. Attack is instant, release is smoothed.
 *
 * Speech is not continuous: every stop consonant is a gap of tens of milliseconds,
 * and a raw RMS drops to zero in every one of them. An orb driven by that flickers.
 * Rising instantly and falling over ~180 ms tracks the onset of a word exactly
 * while riding through the gaps inside it — the same asymmetry a compressor's
 * attack/release uses, and for the same reason.
 */
const RELEASE_PER_MS = 1 / 180;

/**
 * Upstream's own analyser settings for the spectrum, which are NOT the settings the
 * level meter wants.
 *
 * The level analyser below runs `smoothingTimeConstant = 0` on purpose, because the
 * smoothing that matters for a level is the asymmetric attack/release in `read()` and
 * doing it twice makes the value feel late. A spectrum has no such curve: the
 * smoothing IS the presentation, and ElevenLabs' waveform asks for 0.85 with a
 * 256-point transform. Reusing one analyser for both would mean choosing which of
 * the two consumers to make wrong.
 *
 * A second `AnalyserNode` tapping the same source is very close to free — it is a
 * leaf, connected to nothing downstream — so both get what they need.
 */
const SPECTRUM_FFT_SIZE = 256;
const SPECTRUM_SMOOTHING = 0.85;

interface Channel {
  analyser: AnalyserNode
  /** Separate from `analyser`; see SPECTRUM_SMOOTHING. */
  spectrum: AnalyserNode
  spectrumBuf: Uint8Array<ArrayBuffer>
  source: MediaStreamAudioSourceNode
  /** Explicitly over `ArrayBuffer`, not the default `ArrayBufferLike`.
   *  TypeScript 5.7 made typed arrays generic in their backing buffer, and
   *  `getFloatTimeDomainData` will not accept one that might be a
   *  `SharedArrayBuffer`. Naming the buffer type is the fix; a cast would work
   *  too and would hide the next real mismatch. */
  buf: Float32Array<ArrayBuffer>
  /** Smoothed value, 0..1. */
  level: number
  lastReadMs: number
}

export class AudioMeter {
  private channels = new Map<TrackName, Channel>()
  private disposed = false

  constructor(private readonly ctx: AudioContext) {}

  /**
   * Start metering one direction. Safe to call twice for the same track — the
   * second call replaces the first, because a reconnect legitimately hands over a
   * new remote stream for the same logical track.
   */
  attach(track: TrackName, stream: MediaStream): void {
    if (this.disposed) return
    // A stream with no live audio track produces a source node that never fires,
    // which is indistinguishable from silence and would leave the orb flat with no
    // explanation. Refuse instead, so the caller's fallback runs.
    if (stream.getAudioTracks().length === 0) return
    this.detach(track)
    try {
      const source = this.ctx.createMediaStreamSource(stream)
      const analyser = this.ctx.createAnalyser()
      analyser.fftSize = FFT_SIZE
      // Zero, because the smoothing that matters is done in `read()` with an
      // asymmetric release. Letting the node smooth as well would add a second
      // time constant nobody chose and make the orb feel late.
      analyser.smoothingTimeConstant = 0
      source.connect(analyser)

      const spectrum = this.ctx.createAnalyser()
      spectrum.fftSize = SPECTRUM_FFT_SIZE
      spectrum.smoothingTimeConstant = SPECTRUM_SMOOTHING
      source.connect(spectrum)

      this.channels.set(track, {
        analyser,
        spectrum,
        spectrumBuf: new Uint8Array(new ArrayBuffer(spectrum.frequencyBinCount)),
        source,
        buf: new Float32Array(new ArrayBuffer(analyser.fftSize * 4)),
        level: 0,
        lastReadMs: 0,
      })
    } catch {
      /* No meter for this track. `read` returns 0 and the caller degrades. */
    }
  }

  detach(track: TrackName): void {
    const ch = this.channels.get(track)
    if (!ch) return
    try {
      ch.source.disconnect()
      ch.analyser.disconnect()
      ch.spectrum.disconnect()
    } catch {
      /* Already torn down. */
    }
    this.channels.delete(track)
  }

  /**
   * The current level for one track, 0..1, smoothed.
   *
   * Cheap enough to call every frame for both tracks: one `getFloatTimeDomainData`
   * into a reused buffer and a loop over 512 floats.
   *
   * The returned number is PERCEPTUAL, not an amplitude. Raw RMS on speech sits
   * around 0.02–0.08, so a visualiser driven by it directly barely moves; the curve
   * below maps a realistic speech range onto most of 0..1. That is a presentation
   * decision and it belongs here rather than in every component that draws.
   */
  read(track: TrackName, nowMs: number = performance.now()): number {
    const ch = this.channels.get(track)
    if (!ch) return 0

    ch.analyser.getFloatTimeDomainData(ch.buf)
    let sum = 0
    for (let i = 0; i < ch.buf.length; i++) sum += ch.buf[i]! * ch.buf[i]!
    const rms = Math.sqrt(sum / ch.buf.length)

    // ~-40 dBFS floor to ~-6 dBFS ceiling, then a gentle curve so quiet speech is
    // visible without loud speech pinning at 1.
    const norm = Math.min(1, Math.max(0, (rms - 0.005) / 0.14))
    const shaped = Math.pow(norm, 0.65)

    const dtMs = ch.lastReadMs === 0 ? 0 : Math.min(200, nowMs - ch.lastReadMs)
    ch.lastReadMs = nowMs
    // Instant attack, smoothed release. See RELEASE_PER_MS.
    ch.level = shaped >= ch.level ? shaped : Math.max(shaped, ch.level - dtMs * RELEASE_PER_MS)
    return ch.level
  }

  /**
   * Frequency bins for one direction, 0..255 per bin.
   *
   * WHY A CALLER-SUPPLIED ARRAY. This is read every frame by whatever draws, and
   * allocating a `Uint8Array` per frame is how a visualiser becomes the reason a page
   * stutters. The caller keeps one buffer and hands it in; anything past
   * `frequencyBinCount` is left untouched, so a caller with a larger buffer sees its
   * own stale tail rather than zeroes it might mistake for silence.
   *
   * Returns the number of bins written, or 0 when there is nothing to read — no
   * AudioContext, or no stream attached for this track. A drawing component must
   * treat 0 as "use the fallback", not as "silence".
   */
  spectrum(track: TrackName, out: Uint8Array): number {
    const ch = this.channels.get(track)
    if (!ch) return 0
    const n = Math.min(out.length, ch.spectrum.frequencyBinCount)
    if (n === ch.spectrumBuf.length && out.length >= n) {
      ch.spectrum.getByteFrequencyData(ch.spectrumBuf)
      out.set(ch.spectrumBuf.subarray(0, n))
      return n
    }
    // A caller whose buffer is smaller than one frame of bins. Read into ours and
    // copy the prefix, rather than refusing.
    ch.spectrum.getByteFrequencyData(ch.spectrumBuf)
    out.set(ch.spectrumBuf.subarray(0, n))
    return n
  }

  /** Both directions at once, which is what a visualiser actually wants. */
  readAll(nowMs: number = performance.now()): { user: number; assistant: number } {
    return { user: this.read('user', nowMs), assistant: this.read('assistant', nowMs) }
  }

  dispose(): void {
    this.disposed = true
    for (const track of [...this.channels.keys()]) this.detach(track)
  }
}
